import type { AuthSessionView, LoginActivityView } from "../account-types";
import type {
  OAuthAuthorizeInfo,
  RegisteredOAuthApp,
  SsoConfigView,
  SsoRoleRule,
  Me,
  LegalDocumentView,
  TwoFactorEnrollment,
} from "../types";
import { isApiError } from "../errors";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type AuthApiMethods = Pick<
  ApiClient,
  | "me"
  | "login"
  | "requestPhoneOtp"
  | "verifyPhoneOtp"
  | "requestPhoneLinkOtp"
  | "verifyPhoneLinkOtp"
  | "loginSecondFactor"
  | "logout"
  | "listAuthSessions"
  | "revokeAuthSession"
  | "revokeOtherAuthSessions"
  | "listLoginActivity"
  | "signup"
  | "resendVerification"
  | "enrollTwoFactor"
  | "confirmTwoFactor"
  | "requestPasswordReset"
  | "updateProfile"
  | "uploadMyAvatar"
  | "deleteMyAvatar"
  | "requestEmailChange"
  | "confirmEmailChange"
  | "confirmPasswordReset"
  | "requiredLegalDocuments"
  | "pendingLegalDocuments"
  | "acceptLegalDocuments"
  | "registerOAuthApp"
  | "getOAuthAuthorizeInfo"
  | "submitOAuthConsent"
  | "revokeConnectedApp"
  | "getSsoConfig"
  | "saveSsoConfig"
  | "testSsoConnection"
  | "setSsoEnforced"
  | "setSsoJit"
  | "getSsoRoleMapping"
  | "saveSsoRoleMapping"
>;

export function createAuthApi({ request, ws }: HttpTransport): AuthApiMethods {
  return {
    me: () => request<Me>("GET", "/v1/auth/me"),
    async login(input) {
      try {
        const res = await request<{ status: string; challengeToken?: string }>(
          "POST",
          "/v1/auth/login",
          input,
        );
        if (res.status === "2fa_required" && res.challengeToken) {
          return { status: "2fa_required", challengeToken: res.challengeToken };
        }
        return { status: "authenticated" };
      } catch (error) {
        // Mandatory 2FA: a restricted setup session was issued (ADR-0013).
        if (isApiError(error) && error.code === "AUTH_2FA_SETUP_REQUIRED") {
          return { status: "2fa_setup_required" };
        }
        throw error;
      }
    },
    async requestPhoneOtp(input) {
      await request("POST", "/v1/auth/otp/request", input);
    },
    async verifyPhoneOtp(input) {
      try {
        const res = await request<{ status: string; challengeToken?: string }>(
          "POST",
          "/v1/auth/otp/verify",
          input,
        );
        if (res.status === "2fa_required" && res.challengeToken) {
          return { status: "2fa_required", challengeToken: res.challengeToken };
        }
        return { status: "authenticated" };
      } catch (error) {
        if (isApiError(error) && error.code === "AUTH_2FA_SETUP_REQUIRED") {
          return { status: "2fa_setup_required" };
        }
        throw error;
      }
    },
    async requestPhoneLinkOtp(input) {
      await request("POST", "/v1/auth/phone/link/request", input);
    },
    async verifyPhoneLinkOtp(input) {
      await request("POST", "/v1/auth/phone/link/verify", input);
    },
    async loginSecondFactor(input) {
      await request("POST", "/v1/auth/login/2fa", input);
    },
    async logout() {
      await request("POST", "/v1/auth/logout");
    },
    async signup(input) {
      await request("POST", "/v1/auth/signup", input);
    },
    async resendVerification(email) {
      await request("POST", "/v1/auth/resend-verification", { email });
    },
    listAuthSessions: async () =>
      (await request<{ sessions: AuthSessionView[] }>("GET", "/v1/auth/sessions")).sessions,
    revokeAuthSession: async (id) => {
      await request<void>("DELETE", `/v1/auth/sessions/${encodeURIComponent(id)}`);
    },
    revokeOtherAuthSessions: () => request<{ revoked: number }>("DELETE", "/v1/auth/sessions"),
    listLoginActivity: async () =>
      (await request<{ items: LoginActivityView[] }>("GET", "/v1/auth/login-activity")).items,
    enrollTwoFactor: () => request<TwoFactorEnrollment>("POST", "/v1/auth/2fa/enroll"),
    confirmTwoFactor: (code) =>
      request<{ backupCodes: string[] }>("POST", "/v1/auth/2fa/confirm", { code }),
    async requestPasswordReset(email) {
      await request("POST", "/v1/auth/password-reset/request", { email });
    },
    updateProfile: (input) =>
      request<{ displayName?: string; navMode?: "simple" | "advanced" }>(
        "PATCH",
        "/v1/auth/me/profile",
        input,
      ),
    uploadMyAvatar: (file) =>
      request<{ avatarUrl: string }>("POST", "/v1/auth/me/avatar", undefined, {
        body: file,
        contentType: file.type || "application/octet-stream",
      }),
    deleteMyAvatar: async () => {
      await request<void>("DELETE", "/v1/auth/me/avatar");
    },
    requestEmailChange: (input) =>
      request<{ pendingEmail: string; expiresAt: string }>(
        "POST",
        "/v1/auth/me/email-change",
        input,
      ),
    confirmEmailChange: (token) =>
      request<{ status: "email_changed"; email: string }>("POST", "/v1/auth/email-change/confirm", {
        token,
      }),
    async confirmPasswordReset(token, password) {
      await request("POST", "/v1/auth/password-reset/confirm", { token, password });
    },
    async requiredLegalDocuments() {
      return (await request<{ documents: LegalDocumentView[] }>("GET", "/v1/legal/required"))
        .documents;
    },
    async pendingLegalDocuments(id) {
      return (await request<{ pending: LegalDocumentView[] }>("GET", `${ws(id)}/legal/pending`))
        .pending;
    },
    async acceptLegalDocuments(id, accepted) {
      await request("POST", `${ws(id)}/legal/acceptances`, { accepted });
    },
    registerOAuthApp: (input) => request<RegisteredOAuthApp>("POST", "/v1/oauth/apps", input),
    getOAuthAuthorizeInfo: (query) =>
      request<OAuthAuthorizeInfo>("GET", `/v1/oauth/authorize?${query}`),
    submitOAuthConsent: (id, input) =>
      request<{ redirectUrl: string }>("POST", `${ws(id)}/oauth/consent`, input),
    async revokeConnectedApp(id, grantId) {
      await request("DELETE", `${ws(id)}/oauth/grants/${encodeURIComponent(grantId)}`);
    },
    getSsoConfig: (id) => request<SsoConfigView>("GET", `${ws(id)}/settings/security/sso-config`),
    saveSsoConfig: (id, input) =>
      request<SsoConfigView>("PUT", `${ws(id)}/settings/security/sso-config`, input),
    testSsoConnection: (id) =>
      request<{ ok: boolean; detail?: string }>(
        "POST",
        `${ws(id)}/settings/security/sso-config/test-connection`,
      ),
    setSsoEnforced: (id, enforced) =>
      request<SsoConfigView>("PUT", `${ws(id)}/settings/security/sso-config/enforce`, { enforced }),
    setSsoJit: (id, jitEnabled) =>
      request<SsoConfigView>("PUT", `${ws(id)}/settings/security/sso-config/jit`, { jitEnabled }),
    getSsoRoleMapping: (id) =>
      request<{ rules: readonly SsoRoleRule[] }>(
        "GET",
        `${ws(id)}/settings/security/sso-config/role-mapping`,
      ),
    saveSsoRoleMapping: (id, rules) =>
      request<{ rules: readonly SsoRoleRule[] }>(
        "PUT",
        `${ws(id)}/settings/security/sso-config/role-mapping`,
        { rules },
      ),
  };
}
